import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Dialog, DialogContentText, DialogTitle, DialogContent, Typography, Link } from '@mui/material';
import { STORAGE_USER_CONFIRM_EMAIL } from '../../constants/storage';
import { resendEmail } from '../../services/authService';
import { useResponseError } from '../../hooks/useResponseError';
import { CloseModal } from '../Modals/CloseModal';
import { InputText } from '../../elements/InputText';
import { ErrorBox } from '../../elements/ErrorBox';

const ConfirmForm = () => {
  const { responseError, setResponseError } = useResponseError();
  const storedEmail = localStorage.getItem(STORAGE_USER_CONFIRM_EMAIL) || '';
  const [email, setEmail] = useState(storedEmail);
  const [isModalResentOpened, setIsModalResentOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setResponseError('');
    if (!email) {
      setResponseError('Enter the email you used to sign up');
      return;
    }
    setLoading(true);
    try {
      await resendEmail({ email });
      localStorage.setItem(STORAGE_USER_CONFIRM_EMAIL, email);
      setIsModalResentOpened(true);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return <>
    <Box
      sx={{
        maxWidth: '315px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
      <Typography
        align="center"
        variant="h1"
        sx={{
          mb: '16px'
        }}
      >
        Check your email
      </Typography>
      <Typography
        align="center"
        variant="body1"
        sx={{
          mb: '16px'
        }}
      >
        We've sent a verification link to confirm your registration. Open the email and click Verify.
      </Typography>
      <Box sx={{ width: '100%', mb: 2 }}>
        <InputText
          type="email"
          label="Email address"
          placeholder="Email address"
          fullWidth
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />
      </Box>
      {responseError && (
        <ErrorBox sx={{ mb: 2, width: '100%', textAlign: 'left' }}>
          {responseError}
        </ErrorBox>
      )}
      <Button variant="contained" onClick={handleSubmit} disabled={loading} fullWidth>
        {loading ? 'Sending…' : 'Send email again'}
      </Button>
      <Typography sx={{ mt: 2 }} variant="body2">
        Wrong email? <Link component={RouterLink} to="/signup">Sign up again</Link>
        {' · '}
        <Link component={RouterLink} to="/login">Sign in</Link>
      </Typography>
    </Box>
    <Dialog
      sx={[{
        '.MuiDialog-paper': {
          px: '55px',
          py: '10px',
          pt: '20px',
          maxWidth: '440px',
          boxSizing: 'border-box',
          width: '100%'
        }
      }, (theme) => ({
        [theme.breakpoints.down('md')]: {
          '.MuiDialog-paper': {
            px: '15px',
          }
        }
      })]
      }
      open={isModalResentOpened}
      onClose={() => setIsModalResentOpened(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <CloseModal close={() => setIsModalResentOpened(false)} />
      <DialogTitle sx={{
        fontWeight: '700',
        textAlign: 'center'
      }}>
        Verification email sent
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{
          fontWeight: '400',
          color: '#008986',
          mb: '5px',
          textAlign: 'center'
        }}>
          Please check your inbox. If you do not see the message, check spam.
        </DialogContentText>
      </DialogContent>
    </Dialog>
  </>;
};

export default ConfirmForm;
